// ===================================
// TRENCH QUEST - Share Card Generator
// Client-side canvas-based image generation
// ===================================

export interface ShareCardData {
  type: 'game_over' | 'milestone' | 'trade' | 'situation'

  // Game results
  result?: 'win' | 'lose'
  startingAmount?: number
  endingAmount?: number
  multiplier?: number
  daysPlayed?: number
  difficulty?: string

  // Milestone
  milestoneType?: '2x' | '5x' | '10x' | '50x' | '100x'

  // Trade
  tradeType?: 'buy' | 'sell'
  tokenSymbol?: string
  tradeAmount?: number
  profitLoss?: number

  // Situation/Story
  situationTitle?: string
  outcomeBlurb?: string

  // Custom message
  customMessage?: string
}

// Brand colors (Solana-inspired)
const COLORS = {
  background: '#0f0f1a',
  backgroundGradient: '#1a1a2e',
  border: '#9945FF',
  accent: '#14F195',
  profit: '#14F195',
  loss: '#FF6B6B',
  gold: '#FFD700',
  text: '#E0E0E0',
  muted: '#888888',
  white: '#FFFFFF',
}

// Card dimensions (Twitter-optimized 1200x628 or 1:1 for square)
const CARD_WIDTH = 1200
const CARD_HEIGHT = 628

/**
 * Generate a share card image as a data URL
 */
export async function generateShareCard(data: ShareCardData): Promise<string> {
  const canvas = document.createElement('canvas')
  canvas.width = CARD_WIDTH
  canvas.height = CARD_HEIGHT

  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('Could not get canvas context')

  // Draw background
  drawBackground(ctx)

  // Draw border
  drawBorder(ctx)

  // Draw header
  drawHeader(ctx)

  // Draw content based on type
  switch (data.type) {
    case 'game_over':
      drawGameOverContent(ctx, data)
      break
    case 'milestone':
      drawMilestoneContent(ctx, data)
      break
    case 'trade':
      drawTradeContent(ctx, data)
      break
    case 'situation':
      drawSituationContent(ctx, data)
      break
  }

  // Draw footer
  drawFooter(ctx)

  return canvas.toDataURL('image/png')
}

/**
 * Draw gradient background
 */
function drawBackground(ctx: CanvasRenderingContext2D) {
  const gradient = ctx.createLinearGradient(0, 0, CARD_WIDTH, CARD_HEIGHT)
  gradient.addColorStop(0, COLORS.background)
  gradient.addColorStop(1, COLORS.backgroundGradient)
  ctx.fillStyle = gradient
  ctx.fillRect(0, 0, CARD_WIDTH, CARD_HEIGHT)

  // Add subtle grid pattern
  ctx.strokeStyle = 'rgba(153, 69, 255, 0.1)'
  ctx.lineWidth = 1
  const gridSize = 40

  for (let x = 0; x < CARD_WIDTH; x += gridSize) {
    ctx.beginPath()
    ctx.moveTo(x, 0)
    ctx.lineTo(x, CARD_HEIGHT)
    ctx.stroke()
  }

  for (let y = 0; y < CARD_HEIGHT; y += gridSize) {
    ctx.beginPath()
    ctx.moveTo(0, y)
    ctx.lineTo(CARD_WIDTH, y)
    ctx.stroke()
  }
}

/**
 * Draw border
 */
function drawBorder(ctx: CanvasRenderingContext2D) {
  ctx.strokeStyle = COLORS.border
  ctx.lineWidth = 4
  ctx.strokeRect(10, 10, CARD_WIDTH - 20, CARD_HEIGHT - 20)

  // Inner border accent
  ctx.strokeStyle = COLORS.accent
  ctx.lineWidth = 2
  ctx.strokeRect(20, 20, CARD_WIDTH - 40, CARD_HEIGHT - 40)
}

/**
 * Draw header with logo
 */
function drawHeader(ctx: CanvasRenderingContext2D) {
  // Logo area background
  ctx.fillStyle = 'rgba(153, 69, 255, 0.2)'
  ctx.fillRect(20, 20, CARD_WIDTH - 40, 70)

  // Title
  ctx.font = 'bold 36px "Courier New", monospace'
  ctx.fillStyle = COLORS.accent
  ctx.textAlign = 'center'
  ctx.fillText('TRENCH QUEST', CARD_WIDTH / 2, 65)

  // Decorative lines
  ctx.strokeStyle = COLORS.border
  ctx.lineWidth = 2
  ctx.beginPath()
  ctx.moveTo(100, 90)
  ctx.lineTo(CARD_WIDTH - 100, 90)
  ctx.stroke()
}

/**
 * Draw game over content
 */
function drawGameOverContent(ctx: CanvasRenderingContext2D, data: ShareCardData) {
  const isWin = data.result === 'win'
  const centerX = CARD_WIDTH / 2

  // Result title
  ctx.font = 'bold 64px "Courier New", monospace'
  ctx.fillStyle = isWin ? COLORS.profit : COLORS.loss
  ctx.textAlign = 'center'
  ctx.fillText(isWin ? 'WAGMI' : 'NGMI', centerX, 180)

  // Subtitle
  ctx.font = '24px "Courier New", monospace'
  ctx.fillStyle = COLORS.muted
  ctx.fillText(isWin ? 'Mission Accomplished' : 'Better Luck Next Time', centerX, 220)

  // Stats box
  const boxY = 250
  const boxHeight = 200
  ctx.fillStyle = 'rgba(0, 0, 0, 0.5)'
  ctx.fillRect(200, boxY, CARD_WIDTH - 400, boxHeight)
  ctx.strokeStyle = COLORS.border
  ctx.lineWidth = 2
  ctx.strokeRect(200, boxY, CARD_WIDTH - 400, boxHeight)

  // Stats
  ctx.font = '28px "Courier New", monospace'
  ctx.textAlign = 'left'
  const leftX = 240
  const rightX = CARD_WIDTH - 240
  let statY = boxY + 50

  // Starting amount
  ctx.fillStyle = COLORS.muted
  ctx.fillText('Started with:', leftX, statY)
  ctx.fillStyle = COLORS.text
  ctx.textAlign = 'right'
  ctx.fillText(formatCurrency(data.startingAmount || 0), rightX, statY)

  // Ending amount
  statY += 45
  ctx.textAlign = 'left'
  ctx.fillStyle = COLORS.muted
  ctx.fillText('Ended with:', leftX, statY)
  ctx.fillStyle = isWin ? COLORS.profit : COLORS.loss
  ctx.textAlign = 'right'
  ctx.fillText(formatCurrency(data.endingAmount || 0), rightX, statY)

  // Multiplier
  statY += 45
  ctx.textAlign = 'left'
  ctx.fillStyle = COLORS.muted
  ctx.fillText('Multiplier:', leftX, statY)
  ctx.fillStyle = (data.multiplier || 1) >= 1 ? COLORS.profit : COLORS.loss
  ctx.textAlign = 'right'
  ctx.fillText(`${(data.multiplier || 1).toFixed(2)}x`, rightX, statY)

  // Days played
  statY += 45
  ctx.textAlign = 'left'
  ctx.fillStyle = COLORS.muted
  ctx.fillText('Days played:', leftX, statY)
  ctx.fillStyle = COLORS.text
  ctx.textAlign = 'right'
  ctx.fillText(`${data.daysPlayed || 0}`, rightX, statY)

  // Custom message / quote
  if (data.customMessage) {
    ctx.font = 'italic 22px "Courier New", monospace'
    ctx.fillStyle = COLORS.gold
    ctx.textAlign = 'center'
    const message = `"${data.customMessage}"`
    wrapText(ctx, message, centerX, 490, CARD_WIDTH - 200, 28)
  }
}

/**
 * Draw milestone content
 */
function drawMilestoneContent(ctx: CanvasRenderingContext2D, data: ShareCardData) {
  const centerX = CARD_WIDTH / 2

  // Milestone badge
  ctx.font = 'bold 80px "Courier New", monospace'
  ctx.fillStyle = COLORS.gold
  ctx.textAlign = 'center'
  ctx.fillText(data.milestoneType || '2x', centerX, 200)

  // Subtitle
  ctx.font = '32px "Courier New", monospace'
  ctx.fillStyle = COLORS.accent
  ctx.fillText('MILESTONE REACHED', centerX, 250)

  // Stats
  ctx.font = '28px "Courier New", monospace'
  ctx.fillStyle = COLORS.text
  ctx.fillText(`${formatCurrency(data.startingAmount || 0)} → ${formatCurrency(data.endingAmount || 0)}`, centerX, 320)

  // Message
  ctx.font = 'italic 24px "Courier New", monospace'
  ctx.fillStyle = COLORS.muted
  const messages: Record<string, string> = {
    '2x': 'Doubled up. This is just the beginning.',
    '5x': 'Five bagger secured. Few understand.',
    '10x': 'TEN X. The trenches reward the bold.',
    '50x': 'FIFTY X. Generational wealth loading...',
    '100x': 'ONE HUNDRED X. You are the whale now.',
  }
  ctx.fillText(messages[data.milestoneType || '2x'] || 'Gains secured.', centerX, 400)
}

/**
 * Draw trade content
 */
function drawTradeContent(ctx: CanvasRenderingContext2D, data: ShareCardData) {
  const centerX = CARD_WIDTH / 2
  const isProfitable = (data.profitLoss || 0) >= 0

  // Trade type
  ctx.font = 'bold 48px "Courier New", monospace'
  ctx.fillStyle = data.tradeType === 'buy' ? COLORS.profit : COLORS.loss
  ctx.textAlign = 'center'
  ctx.fillText(data.tradeType === 'buy' ? 'BOUGHT' : 'SOLD', centerX, 180)

  // Token
  ctx.font = 'bold 72px "Courier New", monospace'
  ctx.fillStyle = COLORS.accent
  ctx.fillText(data.tokenSymbol || 'TOKEN', centerX, 270)

  // Amount
  ctx.font = '32px "Courier New", monospace'
  ctx.fillStyle = COLORS.text
  ctx.fillText(formatCurrency(data.tradeAmount || 0), centerX, 330)

  // P&L if applicable
  if (data.profitLoss !== undefined && data.profitLoss !== 0) {
    ctx.font = 'bold 36px "Courier New", monospace'
    ctx.fillStyle = isProfitable ? COLORS.profit : COLORS.loss
    const plText = isProfitable ? `+${formatCurrency(data.profitLoss)}` : formatCurrency(data.profitLoss)
    ctx.fillText(plText, centerX, 400)
  }
}

/**
 * Draw situation/story content
 */
function drawSituationContent(ctx: CanvasRenderingContext2D, data: ShareCardData) {
  const centerX = CARD_WIDTH / 2

  // Title
  ctx.font = 'bold 36px "Courier New", monospace'
  ctx.fillStyle = COLORS.accent
  ctx.textAlign = 'center'
  ctx.fillText(data.situationTitle || 'An Interesting Situation', centerX, 180)

  // Divider
  ctx.strokeStyle = COLORS.border
  ctx.lineWidth = 2
  ctx.beginPath()
  ctx.moveTo(300, 210)
  ctx.lineTo(CARD_WIDTH - 300, 210)
  ctx.stroke()

  // Outcome blurb
  if (data.outcomeBlurb) {
    ctx.font = '24px "Courier New", monospace'
    ctx.fillStyle = COLORS.text
    wrapText(ctx, data.outcomeBlurb, centerX, 280, CARD_WIDTH - 200, 32)
  }

  // Custom message
  if (data.customMessage) {
    ctx.font = 'italic 22px "Courier New", monospace'
    ctx.fillStyle = COLORS.gold
    wrapText(ctx, `"${data.customMessage}"`, centerX, 450, CARD_WIDTH - 200, 28)
  }
}

/**
 * Draw footer
 */
function drawFooter(ctx: CanvasRenderingContext2D) {
  const footerY = CARD_HEIGHT - 50

  // Footer line
  ctx.strokeStyle = COLORS.border
  ctx.lineWidth = 2
  ctx.beginPath()
  ctx.moveTo(100, footerY - 20)
  ctx.lineTo(CARD_WIDTH - 100, footerY - 20)
  ctx.stroke()

  // URL and social
  ctx.font = '20px "Courier New", monospace'
  ctx.fillStyle = COLORS.muted
  ctx.textAlign = 'center'
  ctx.fillText('trench-quest.web.app', CARD_WIDTH / 2, footerY + 10)
}

/**
 * Helper: Format currency
 */
function formatCurrency(value: number): string {
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(2)}M`
  if (value >= 1_000) return `$${(value / 1_000).toFixed(1)}K`
  return `$${value.toFixed(0)}`
}

/**
 * Helper: Wrap text to fit width
 */
function wrapText(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  maxWidth: number,
  lineHeight: number
) {
  const words = text.split(' ')
  let line = ''
  let currentY = y

  for (const word of words) {
    const testLine = line + word + ' '
    const metrics = ctx.measureText(testLine)

    if (metrics.width > maxWidth && line !== '') {
      ctx.fillText(line.trim(), x, currentY)
      line = word + ' '
      currentY += lineHeight
    } else {
      line = testLine
    }
  }
  ctx.fillText(line.trim(), x, currentY)
}

/**
 * Download the share card image
 */
export function downloadShareCard(dataUrl: string, filename: string = 'trench-quest-share.png') {
  const link = document.createElement('a')
  link.href = dataUrl
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

/**
 * Open Twitter with share intent
 */
export function shareToTwitter(text: string, url?: string) {
  const tweetText = encodeURIComponent(text)
  const tweetUrl = url ? `&url=${encodeURIComponent(url)}` : ''
  const twitterIntent = `https://twitter.com/intent/tweet?text=${tweetText}${tweetUrl}`
  window.open(twitterIntent, '_blank', 'width=550,height=420')
}

/**
 * Generate share text based on card data
 */
export function generateShareText(data: ShareCardData): string {
  switch (data.type) {
    case 'game_over':
      if (data.result === 'win') {
        return `I just conquered the trenches in @TrenchQuest!

Started with ${formatCurrency(data.startingAmount || 0)}, ended with ${formatCurrency(data.endingAmount || 0)} (${(data.multiplier || 1).toFixed(2)}x)

WAGMI`
      } else {
        return `The trenches claimed another soul in @TrenchQuest

${formatCurrency(data.startingAmount || 0)} → ${formatCurrency(data.endingAmount || 0)}

NGMI... or maybe next time?`
      }

    case 'milestone':
      return `Just hit ${data.milestoneType} in @TrenchQuest!

${formatCurrency(data.startingAmount || 0)} → ${formatCurrency(data.endingAmount || 0)}

The trenches reward the bold.`

    case 'trade':
      return `${data.tradeType === 'buy' ? 'Aped into' : 'Sold'} $${data.tokenSymbol} in @TrenchQuest

${data.profitLoss && data.profitLoss > 0 ? `+${formatCurrency(data.profitLoss)} profit` : ''}

Are you built for the trenches?`

    case 'situation':
      return `${data.situationTitle || 'Wild moment'} in @TrenchQuest

${data.outcomeBlurb?.slice(0, 100) || ''}...

Play at trench-quest.web.app`

    default:
      return 'Playing @TrenchQuest - the AI-powered crypto trading adventure!\n\ntrench-quest.web.app'
  }
}
